(ns io.daskul.webapp.widgets.dashboard.pnl-summary
  "P&L summary widget — a compact income statement in one card:
   Revenue − Expenses = Net Profit, with the Net Profit trend arrow. Reads as a
   real P&L instead of three disconnected stat cards.

   Expects a metric map with :widget :pnl-summary shape:
     {:widget :pnl-summary
      :key    \"pnl-summary\"
      :rows   [{:label \"Revenue\"  :value 4000}
               {:label \"Expenses\" :value 0}]   ; rows after the first are subtracted
      :total  {:label \"Net Profit\" :value 4000 :previous-value 3200}
      :format :currency                          ; :number | :currency | :percent
      :description \"...\"}"
  (:require
   [uix.core :refer [defui $]]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.ui.icons :as icons]))

(defn- format-value [value fmt]
  (case (keyword (or fmt :number))
    :currency (.toLocaleString (js/Number. value) "en-PH"
                #js {:style "currency" :currency "PHP"
                     :minimumFractionDigits 0 :maximumFractionDigits 0})
    :percent  (str (.toFixed (* 100 value) 1) "%")
    (str value)))

(defn- trend
  "Arrow colour + icon for the total vs its previous-value, or nil when there's
   nothing to compare."
  [value previous-value]
  (when (some? previous-value)
    (cond
      (> value previous-value) {:color "teal" :icon icons/icon-trending-up}
      (< value previous-value) {:color "red"  :icon icons/icon-trending-down}
      :else                    {:color "gray" :icon icons/icon-minus})))

(defui pnl-summary
  "Renders the headline P&L as a one-card income statement: each row's value on
   the right, rows after the first shown as subtractions, a rule, then the
   bold total with its trend arrow and an optional description tooltip."
  [{:keys [rows total format description footnote]}]
  (let [fmt (keyword (or format :number))
        {:keys [label value previous-value]} total
        t   (trend value previous-value)]
    ($ ui/paper {:p "lg" :radius "md" :withBorder true :style {:height "100%"}}
      ($ ui/stack {:gap 8}
        (for [r rows]
          ($ ui/group {:key (:label r) :justify "space-between" :align "center" :wrap "nowrap"}
            ($ ui/text {:size "xs" :tt "uppercase" :fw 600 :c "dimmed"}
              (:label r))
            ($ ui/text {:size "md" :fw 600 :lh 1.2
                        :style {:fontFamily "Sora, sans-serif" :fontVariantNumeric "tabular-nums"}}
              (format-value (:value r) fmt))))
        ($ ui/divider {:my 2})
        ($ ui/group {:justify "space-between" :align "center" :wrap "nowrap"}
          ($ ui/group {:gap 6 :align "center" :wrap "nowrap"}
            ($ ui/text {:size "xs" :tt "uppercase" :fw 700} label)
            (when description
              ($ ui/tooltip {:label description :multiline true :w 220
                             :position "top-end" :withArrow true}
                ($ ui/text {:c "dimmed" :style #js {:cursor "help"} :lh 1}
                  ($ icons/icon-info-circle {:size 14 :stroke 1.5})))))
          ($ ui/group {:gap 6 :align "flex-end" :wrap "nowrap"}
            ($ ui/text {:size "1.5rem" :fw 700 :lh 1.2
                        :style {:fontFamily "Sora, sans-serif" :fontVariantNumeric "tabular-nums"
                                ;; negative Net Profit in red — a loss shouldn't read
                                ;; like a gain (accounting convention).
                                :color (when (and (= fmt :currency) (number? value) (neg? value))
                                         "var(--mantine-color-red-6)")}}
              (format-value value fmt))
            (when t
              ($ ui/text {:c (:color t) :fw 600 :lh 1.2}
                ($ (:icon t) {:size 16 :stroke 2})))))
        ;; Quiet awareness footnote (e.g. voided this period) — hidden when zero,
        ;; since there's nothing to be aware of.
        (when (and footnote (pos? (:value footnote)))
          ($ ui/text {:size "xs" :c "dimmed" :mt 2}
            (str (:label footnote) ": "
                 (format-value (:value footnote) (keyword (or (:format footnote) fmt)))
                 (when (:note footnote) (str " · " (:note footnote))))))))))
